import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router';
import { DialogType } from '../../../@types';
import {
  LS_KEYS,
  PAYMENT_METHOD_FILTER_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  PAYMENT_TRANSACTION_TYPE_OPTIONS,
  WALLET_TRANSACTION_TYPE_OPTIONS,
} from '../../../constants';
import { PAYMENT_GATEWAY } from '../../../constants/enums';
import { AdditionalQueryParams } from '../../../hooks/usePagination';
import { useMarketplace } from '../../../hooks/useMarketplace';
import usePersist from '../../../hooks/usePersist';
import { normalizePaymentMethodFilter } from '../../../utils';
import FilterDialogLayout from '../../layouts/FilterDialogLayout';
import FormSelect from '../../molecules/FormSelect';

const DEFAULT_FORM_DATA = {
  paymentMethod: 'ALL',
  type: undefined,
  status: undefined,
};

type WalletTransactionFormData = {
  paymentMethod?: string;
  type?: string;
  status?: string;
};

type Props = DialogType & {
  changeQuery: (data: AdditionalQueryParams) => void;
};

const FilterWalletTransactions = ({ closeModal, changeQuery }: Props) => {
  // TODO: Move the filtering and persisting logic into a reusable hook since we are using it in multiple components
  const { setPersistData, getPersistData, deletePersistData } = usePersist(false);
  const { pathname } = useLocation();
  const { config } = useMarketplace();
  const formDataKey = `${LS_KEYS.appFormFilter}-${pathname}`;

  const getSavedData = () => getPersistData<WalletTransactionFormData>(formDataKey) || DEFAULT_FORM_DATA;

  const { control, handleSubmit } = useForm<WalletTransactionFormData>({
    defaultValues: getSavedData(),
  });

  const onFormSubmit = (data: WalletTransactionFormData) => {
    const normalizedData = normalizePaymentMethodFilter(data);

    setPersistData<WalletTransactionFormData>(formDataKey, data);
    changeQuery(normalizedData);
    closeModal?.();
  };

  const handleResetFilter = () => {
    deletePersistData(formDataKey);
    changeQuery(normalizePaymentMethodFilter(DEFAULT_FORM_DATA));
    closeModal?.();
  };

  const isPlayerWallet = pathname.includes('players');
  const hasCryptoPaymentGateway = config?.marketplaceConfig?.paymentGateway === PAYMENT_GATEWAY.CRYPTO;
  const isPlayerWalletTransaction = isPlayerWallet && pathname.includes('wallet-transaction');
  const shouldShowPaymentMethodFilter = isPlayerWallet && hasCryptoPaymentGateway;

  let transactionTypeOptions;

  if (isPlayerWalletTransaction) {
    transactionTypeOptions = WALLET_TRANSACTION_TYPE_OPTIONS;
  } else {
    transactionTypeOptions = isPlayerWallet
      ? PAYMENT_TRANSACTION_TYPE_OPTIONS
      : PAYMENT_TRANSACTION_TYPE_OPTIONS.filter((option) => option.id !== 'bonus');
  }

  return (
    <FilterDialogLayout
      label='transactions'
      onClose={() => {
        closeModal?.();
      }}
      onSave={handleSubmit(onFormSubmit)}
      onReset={handleResetFilter}
    >
      {shouldShowPaymentMethodFilter && (
        <FormSelect
          name='paymentMethod'
          label='Payment method'
          control={control}
          options={PAYMENT_METHOD_FILTER_OPTIONS}
        />
      )}
      <FormSelect name='type' label='Type' control={control} options={transactionTypeOptions} />
      <FormSelect name='status' label='Status' control={control} options={PAYMENT_STATUS_OPTIONS} />
    </FilterDialogLayout>
  );
};

export default FilterWalletTransactions;
